import { Link } from '@tanstack/react-router'
import { useTranslation } from 'react-i18next'
import {
  useGetCarouselInvestigations,
  useGetPaginatedInvestigations,
  useGetInvestigatorsCount,
} from '@/application/services/public-investigation.service'
import { Button } from '@/ui/components/common/Button'
import { InvestigationsCarousel } from '@/ui/components/public/InvestigationsCarousel'

/**
 * The pre-redesign homepage (carousel + hero + manifesto + KPI stats + CTA),
 * extracted verbatim from the former `IndexComponent` — see `ui/pages/index.tsx`,
 * which now just switches between this and `NewsHomePage` based on the
 * `homepageLayout` site setting.
 */
export function ClassicHomePage() {
  const { t } = useTranslation()
  const { data: carouselInvestigations, isLoading: isLoadingCarousel } =
    useGetCarouselInvestigations(3, 7)
  const { data: activeData } = useGetPaginatedInvestigations({
    page: 1,
    limit: 1,
    status: 'active',
  })
  const { data: totalData } = useGetPaginatedInvestigations({ page: 1, limit: 1 })
  const { data: closedData } = useGetPaginatedInvestigations({
    page: 1,
    limit: 1,
    status: 'inactive',
  })

  const activeInvestigations = activeData?.total ?? 0
  const totalInvestigations = totalData?.total ?? 0
  const closedInvestigations = closedData?.total ?? 0

  const { data: investigatorsCount } = useGetInvestigatorsCount()
  const totalInvestigators = investigatorsCount ?? 0

  return (
    <div className="m-auto w-full max-w-[100rem] bg-card grow">
      {/* ─── Recent Investigations Carousel (full-width, no label) ─── */}
      <div>
        {isLoadingCarousel ? (
          <div className="w-full aspect-video bg-muted animate-pulse" />
        ) : carouselInvestigations && carouselInvestigations.length > 0 ? (
          <InvestigationsCarousel
            investigations={carouselInvestigations}
            className="rounded-none"
          />
        ) : null}
      </div>

      {/* ─── Hero Section ─── */}
      <section className="flex flex-col items-center text-center px-8 pt-8 pb-20">
        {/* Main heading */}
        <h1 className="text-5xl md:text-7xl font-serif font-black text-foreground leading-tight">
          {t('home.hero_title')}
        </h1>

        {/* Quote — just below the title */}
        <p className="font-display text-xl md:text-2xl text-muted-foreground text-center mt-2">
          {t('home.manifesto_quote')}
        </p>

        {/* Divider */}
        <div className="flex items-center gap-3 my-8 justify-center">
          <span className="h-px w-16 bg-primary/40" />
          <span className="text-primary">✦</span>
          <span className="h-px w-16 bg-primary/40" />
        </div>

        {/* Subtitle */}
        <p className="text-lg md:text-xl text-muted-foreground mt-2 leading-relaxed">
          {t('home.hero_subtitle')}
        </p>

        {/* Stats row */}
        <div className="flex flex-wrap gap-8 justify-center mt-10 text-center">
          <div>
            <div className="text-3xl font-serif font-black text-primary">{totalInvestigations}</div>
            <div className="text-xs text-muted-foreground mt-1 uppercase tracking-wider">
              {t('home.stats_total_investigations')}
            </div>
          </div>
          <div className="h-12 w-px bg-border self-center hidden sm:block" />
          <div>
            <div className="text-3xl font-serif font-black text-primary">
              {activeInvestigations}
            </div>
            <div className="text-xs text-muted-foreground mt-1 uppercase tracking-wider">
              {t('home.stats_investigations')}
            </div>
          </div>
          <div className="h-12 w-px bg-border self-center hidden sm:block" />
          <div>
            <div className="text-3xl font-serif font-black text-primary">
              {closedInvestigations}
            </div>
            <div className="text-xs text-muted-foreground mt-1 uppercase tracking-wider">
              {t('home.stats_closed_investigations')}
            </div>
          </div>
          <div className="h-12 w-px bg-border self-center hidden sm:block" />
          <div>
            <div className="text-3xl font-serif font-black text-primary">{totalInvestigators}</div>
            <div className="text-xs text-muted-foreground mt-1 uppercase tracking-wider">
              {t('home.stats_investigators')}
            </div>
          </div>
        </div>

        {/* CTA */}
        <div className="mt-10">
          <Link to="/investigations">
            <Button size="lg">{t('home.hero_cta')}</Button>
          </Link>
        </div>
      </section>
    </div>
  )
}
